import { useState, useMemo, useEffect } from "react";
import { Check, ChevronsUpDown, Stethoscope, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTreatmentCategories } from "@/hooks/useTreatmentCategories";
import { useTreatmentsByCategory } from "@/hooks/useTreatments";

interface TreatmentSelectorProps {
  category: string;
  subcategory: string;
  onCategoryChange: (value: string) => void;
  onSubcategoryChange: (value: string) => void;
  otherCategory: string;
  otherSubcategory: string;
  onOtherCategoryChange: (value: string) => void;
  onOtherSubcategoryChange: (value: string) => void;
  required?: boolean;
  error?: string;
}

export function TreatmentSelector({
  category,
  subcategory,
  onCategoryChange,
  onSubcategoryChange,
  otherCategory,
  otherSubcategory,
  onOtherCategoryChange,
  onOtherSubcategoryChange,
  required,
  error,
}: TreatmentSelectorProps) {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [subcategoryOpen, setSubcategoryOpen] = useState(false);

  const { data: categories, isLoading: categoriesLoading } = useTreatmentCategories();
  const { data: treatments, isLoading: treatmentsLoading } = useTreatmentsByCategory(
    (category && category !== "other" ? category : undefined) as Parameters<typeof useTreatmentsByCategory>[0]
  );

  // Reset subcategory when category changes
  useEffect(() => {
    if (category !== "other") {
      onSubcategoryChange("");
      onOtherSubcategoryChange("");
    }
  }, [category]);

  const selectedCategory = useMemo(
    () => categories?.find((c) => c.id === category),
    [categories, category]
  );

  const selectedTreatment = useMemo(
    () => treatments?.find((t) => t.slug === subcategory),
    [treatments, subcategory]
  );

  const showOtherCategoryInput = category === "other";
  const showOtherSubcategoryInput = subcategory === "other";
  const showSubcategorySelector = category && category !== "other";

  return (
    <div className="space-y-4">
      {/* Category Selector */}
      <div className="space-y-2">
        <Label>Treatment Category {required && "*"}</Label>
        <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={categoryOpen}
              className={cn(
                "w-full justify-between font-normal",
                !category && "text-muted-foreground",
                error && "border-destructive"
              )}
              disabled={categoriesLoading}
            >
              {categoriesLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </span>
              ) : selectedCategory ? (
                <span className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-primary" />
                  <span>{selectedCategory.title}</span>
                  <span className="text-muted-foreground text-xs">
                    ({selectedCategory.treatmentCount} treatments)
                  </span>
                </span>
              ) : category === "other" ? (
                <span className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-primary" />
                  Other
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4" />
                  Select treatment category
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0 bg-popover z-50" align="start" sideOffset={4}>
            <Command>
              <CommandInput placeholder="Search categories..." className="h-9" />
              <CommandList>
                <CommandEmpty>No category found.</CommandEmpty>
                <CommandGroup heading="Treatment Categories">
                  {categories?.map((cat) => (
                    <CommandItem
                      key={cat.id}
                      value={cat.title}
                      onSelect={() => {
                        onCategoryChange(cat.id);
                        setCategoryOpen(false);
                      }}
                    >
                      <div className="flex flex-col flex-1">
                        <span className="font-medium">{cat.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {cat.description}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {cat.treatmentCount}
                      </span>
                      <Check
                        className={cn(
                          "ml-2 h-4 w-4",
                          category === cat.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandGroup>
                  <CommandItem
                    value="other"
                    onSelect={() => {
                      onCategoryChange("other");
                      setCategoryOpen(false);
                    }}
                  >
                    <span className="flex-1">Other / Not Listed</span>
                    <Check
                      className={cn(
                        "ml-2 h-4 w-4",
                        category === "other" ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
      {showOtherCategoryInput && (
        <div className="space-y-2">
          <Label htmlFor="otherCategory">Please specify your treatment category</Label>
          <Input
            id="otherCategory"
            placeholder="e.g., Bariatric Surgery, Neurology..."
            value={otherCategory}
            onChange={(e) => onOtherCategoryChange(e.target.value)}
            maxLength={100}
            autoComplete="off"
          />
        </div>
      )}

      {/* Subcategory Selector */}
      {showSubcategorySelector && (
        <div className="space-y-2">
          <Label>Specific Treatment</Label>
          <Popover open={subcategoryOpen} onOpenChange={setSubcategoryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={subcategoryOpen}
                className={cn(
                  "w-full justify-between font-normal",
                  !subcategory && "text-muted-foreground"
                )}
                disabled={treatmentsLoading}
              >
                {treatmentsLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading treatments...
                  </span>
                ) : selectedTreatment ? (
                  <span>{selectedTreatment.name}</span>
                ) : subcategory === "other" ? (
                  <span>Other</span>
                ) : (
                  <span>Select specific treatment (optional)</span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0 bg-popover z-50" align="start" sideOffset={4}>
              <Command>
                <CommandInput placeholder="Search treatments..." className="h-9" />
                <CommandList>
                  <CommandEmpty>No treatment found.</CommandEmpty>
                  <CommandGroup heading="Available Treatments">
                    {treatments?.map((treatment) => (
                      <CommandItem
                        key={treatment.id}
                        value={treatment.name}
                        onSelect={() => {
                          onSubcategoryChange(treatment.slug);
                          setSubcategoryOpen(false);
                        }}
                      >
                        <span className="flex-1">{treatment.name}</span>
                        <Check
                          className={cn(
                            "ml-2 h-4 w-4",
                            subcategory === treatment.slug ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandGroup>
                    <CommandItem
                      value="other specific treatment"
                      onSelect={() => {
                        onSubcategoryChange("other");
                        setSubcategoryOpen(false);
                      }}
                    >
                      <span className="flex-1">Other / Not Listed</span>
                      <Check
                        className={cn(
                          "ml-2 h-4 w-4",
                          subcategory === "other" ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Other Subcategory Input */}
      {showOtherSubcategoryInput && (
        <div className="space-y-2">
          <Label htmlFor="otherSubcategory">Please specify the treatment</Label>
          <Input
            id="otherSubcategory"
            placeholder="e.g., Knee Arthroscopy, Liver Biopsy..."
            value={otherSubcategory}
            onChange={(e) => onOtherSubcategoryChange(e.target.value)}
            maxLength={200}
            autoComplete="off"
          />
        </div>
      )}
    </div>
  );
}
